import React, { ReactElement, useState, useCallback, useMemo, useEffect } from "react";
import {
    Drawer,
    DrawerBody,
    DrawerFooter,
    DrawerHeader,
    DrawerHeaderTitle,
} from "src/components/Drawer";
import { QualityHubActionFormDrawerProps, QualityHubActionFormDrawerState } from "./QualityHubActionFormDrawer.types";
import { createTaskListActionAsync, createTaskListItemAsync, updateTaskListItemAsync } from "src/api";
import { TaskActionModelUpdateHandler, TaskActionFormModel } from "../types";
import { QualityHubActionForm } from "../QualityHubActionForm";
import { localize, localizeFormat } from "src/l10n";
import { SpintrTypes } from "src/typings";
import { UnstyledButton } from "src/ui";
import { SmallBodyBolder } from "src/components/Text";
import { PrimaryButton } from "@fluentui/react";
import { useDispatch, useSelector } from "react-redux";
import {
    actionListSourceCacheKey,
    findActionListForSource,
    taskListItemCreated,
    taskListItemUpdated,
} from "src/redux";
import { Conditional } from "src/components/Conditional";
import SpintrLoader from "src/ui/components/Loader";
import { setConfirmPopup } from "src/popups/actions";
import { useNodeMembership } from "src/hooks";

const initialModel: TaskActionFormModel = {
    title: "",
    description: "",
    sortOrder: 0,
    contentStatus: SpintrTypes.ContentStatus.Published,
    assignees: [],
};

function createInitialModel(): TaskActionFormModel {
    return {
        ...initialModel,
        assignees: [],
        contentStatus: SpintrTypes.ContentStatus.Published,
    };
}

/**
 * QualityHubActionFormDrawer component
 *
 * @param {QualityHubActionFormDrawerProps} props - The component props
 * @returns {ReactElement | null} The rendered component
 */
function QualityHubActionFormDrawer({
    action,
    listId,
    onDismiss,
    open,
    selectedNodeId,
    task,
}: QualityHubActionFormDrawerProps): ReactElement | null {
    const { isManager } = useNodeMembership();
    const dispatch = useDispatch();

    // When creating a follow-up action from a task, the action lives on a
    // node-owned action list that we don't yet have the id for. Resolve it
    // up-front so we can read the action list's owner to scope the user
    // search shown by the QualityHubActionForm's Delegera field. We cache
    // the (sourceListId, nodeId) → actionListId mapping in Redux so
    // subsequent drawer opens for the same combo skip the round-trip.
    const cacheKey = task && !action
        ? actionListSourceCacheKey(task.listId, selectedNodeId)
        : undefined;

    const {
        cachedActionListId,
        isSmallViewMode,
    } = useSelector<Spintr.AppState, {
        cachedActionListId: number | undefined;
        isSmallViewMode: boolean;
    }>(
        (appState) => ({
            cachedActionListId: cacheKey !== undefined
                ? appState.qualityHub.actionListIdBySource[cacheKey]
                : undefined,
            isSmallViewMode: appState.ui.isSmallViewMode,
        }),
    );

    const lookupListId = listId
        ?? action?.listId
        ?? cachedActionListId;

    const taskList = useSelector<Spintr.AppState, Spintr.TaskList | undefined>(
        (appState) => lookupListId !== undefined
            ? appState.qualityHub.listsById[lookupListId]
            : undefined,
    );

    console.log(taskList);

    const [model, setModel] = useState<TaskActionFormModel>(createInitialModel);

    const [state, setState] = useState<QualityHubActionFormDrawerState>({
        isSaving: false,
    });
    const { isSaving } = state;

    const l10n = useMemo(() => ({
        createAction: localize("QH_CREATE_ACTION"),
        cancel: localize("Avbryt"),
        save: localize("Spara"),
    }), []);

    const onModelChanged = useCallback<TaskActionModelUpdateHandler>(
        (updater) => setModel((model) => updater(model)),
        [setModel]);

    const onDismissing = useCallback(() => {
        if (task && !action) {
            dispatch(setConfirmPopup({
                isOpen: true,
                message: localizeFormat("FOLLOW_UP_ABORTED_WARNING_F", task.title),
                onConfirm: () => {
                    setModel(createInitialModel());
                    onDismiss(false);
                }
            }));
            return;
        }

        setModel(createInitialModel());
        onDismiss(false);
    }, [onDismiss, setModel, task, action]);

    
    const onCancelClicked = onDismissing;

    const onOpenChanged = useCallback((isOpen: boolean) => {
        if (isOpen) {
            return;
        }

        onDismissing();
    }, [onDismissing]);

    const onSubmitting = useCallback(async () => {
        if (isSaving) {
            return;
        }

        if (model.title.trim().length === 0) {
            return;
        }

        setState((prevState) => ({ ...prevState, isSaving: true }));

        const assigneeIds = model.assignees?.map(a => a.key as number) ?? [];

        let savedAction: Spintr.TaskListTask | undefined;
        try {
            if (!action && !task && listId) {
                savedAction = await createTaskListItemAsync({
                    ...model,
                    title: model.title.trim(),
                    listId: listId,
                    tagIds: [],
                    assigneeIds,
                });
            } else if (task && !action) {
                savedAction = await createTaskListActionAsync(task.id, {
                    ...model,
                    assigneeIds,
                });
            } else if (action) {
                savedAction = await updateTaskListItemAsync(action.id, {
                    ...model,
                    listId: action.listId,
                    tagIds: [],
                    assigneeIds,
                });
            } else {
                console.error("How did we get here..?");
            }
        } catch (err) {
            console.error("Error creating action:", err);
            setState((prevState) => ({ ...prevState, isSaving: false }));
            return;
        }

        setModel(createInitialModel());
        setState((prevState) => ({ ...prevState, isSaving: false }));
        dispatch(!action
            ? taskListItemCreated(savedAction)
            : taskListItemUpdated(savedAction));
        onDismiss?.(true);
    }, [action, isSaving, model, setState, task?.id]);

    useEffect(() => {
        if (!action) {
            setModel(createInitialModel());
            return;
        }

        setModel({
            ...initialModel,
            ...action,
            assignees: (action.assignments ?? [])
                .map((a) => ({
                    key: a.assignee.id,
                    name: a.assignee.name,
                    imageUrl: a.assignee.imageUrl,
                    type: SpintrTypes.UberType.User,
                })),
        });
    }, [action, open, setModel]);

    useEffect(() => {
        // Only resolve the action list when creating a follow-up from a
        // task — the editing path already has action.listId in scope, and
        // the standalone create path is keyed by the listId prop. Skip the
        // round-trip if the (sourceListId, nodeId) mapping is already
        // cached from a previous resolution.
        if (!open || !task || action || cachedActionListId !== undefined) {
            return;
        }

        const dispatched = dispatch(findActionListForSource(task.listId, selectedNodeId)) as
            unknown as Promise<unknown>;

        dispatched.catch(() => {
            // Resolution failed — drawer falls back to hiding the Delegera
            // scope. The submit endpoint still creates the action list on
            // its own.
        });
    }, [open, task, action, selectedNodeId, cachedActionListId, dispatch]);

    const titleText = action?.title
        ? `${localize("Redigera")} '${action.title}'`
        : (!task ? l10n.createAction : localizeFormat("CREATE_FOLLOW_UP_ON_X", task.title));

    return (
        <Drawer className="QualityHubActionFormDrawer"
                onOpenChange={onOpenChanged}
                open={open}
                position="end"
                size={isSmallViewMode ? "full" : "medium"}>
            <DrawerHeader>
                <DrawerHeaderTitle>
                    {titleText}
                </DrawerHeaderTitle>
            </DrawerHeader>
            <DrawerBody>
                <Conditional condition={isSaving}>
                    <SpintrLoader />
                </Conditional>
                <Conditional condition={!isSaving}>
                    <QualityHubActionForm canChangeContentStatus={isManager}
                                          canRequireVerification={isManager}
                                          listOwnerId={taskList?.targets?.map(t => t.id)?.[0]}
                                          model={model}
                                          onModelChange={onModelChanged} />
                </Conditional>
            </DrawerBody>
            <DrawerFooter className="QualityHubActionFormDrawer-footer">
                <UnstyledButton disabled={isSaving}
                                onClick={onCancelClicked}>
                    <SmallBodyBolder color="contentLight">
                        {l10n.cancel}
                    </SmallBodyBolder>
                </UnstyledButton>
                <PrimaryButton className="QualityHubTaskFormDrawer-save"
                               disabled={isSaving}
                               onClick={onSubmitting}
                               type="submit">
                    {l10n.save}
                </PrimaryButton>
            </DrawerFooter>
        </Drawer>
    );
}

export default QualityHubActionFormDrawer;

